import { StandardEvents } from '@shopify/events';

if (!customElements.get('ph-store-header')) {
  customElements.define('ph-store-header', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.prepareLogin();
      this.restoreQuantity();
      this.addEventListener('click', event => {
        if (event.target.closest('[data-storefront-login]')) this.prepareLogin();
      }, options);
      document.addEventListener('change', () => this.prepareLogin(), options);
      document.addEventListener('input', () => this.prepareLogin(), options);
      document.addEventListener('quantity-selector:update', () => this.prepareLogin(), options);
      document.addEventListener(StandardEvents.productSelect, event => {
        event.promise?.then(() => this.prepareLogin()).catch(() => {});
      }, options);
      document.addEventListener(StandardEvents.cartLinesUpdate, event => {
        event.promise?.then(({ cart, detail }) => {
          const count = cart?.totalQuantity ?? detail?.itemCount;
          if (Number.isSafeInteger(count)) this.updateCount(count);
          else this.refreshCount();
        }).catch(() => {});
      }, options);
      window.addEventListener('pageshow', event => {
        this.prepareLogin();
        if (event.persisted) this.refreshCount();
      }, options);
      document.addEventListener('click', event => {
        for (const menu of this.querySelectorAll('details[open]')) {
          if (!menu.contains(event.target)) menu.open = false;
        }
      }, options);
      this.addEventListener('keydown', event => {
        if (event.key !== 'Escape') return;
        const menu = event.target.closest('details[open]');
        if (menu) { menu.open = false; menu.querySelector('summary').focus(); }
      }, options);
      for (const menu of this.querySelectorAll('details')) {
        menu.addEventListener('toggle', () => {
          menu.querySelector('summary').setAttribute('aria-expanded', String(menu.open));
          if (menu.open) for (const other of this.querySelectorAll('details[open]')) {
            if (other !== menu) other.open = false;
          }
        }, options);
      }
    }

    disconnectedCallback() { this.controller?.abort(); this.controller = null; }

    productForm() {
      return document.querySelector('pharmacy-purchase-controls[data-enabled] product-form-component form');
    }

    prepareLogin() {
      const url = new URL(location.href);
      const form = this.productForm();
      if (form) {
        const variant = form.querySelector('[name="id"]')?.value;
        const quantity = Number(form.querySelector('[name="quantity"]')?.value);
        if (/^\d+$/.test(variant || '')) url.searchParams.set('variant', variant);
        if (Number.isSafeInteger(quantity) && quantity > 0) url.searchParams.set('ph_login_qty', String(quantity));
      }
      const returnTo = url.pathname + url.search + url.hash;
      for (const link of this.querySelectorAll('[data-storefront-login]')) {
        link.href = '/customer_authentication/login?return_to=' + encodeURIComponent(returnTo);
      }
    }

    async restoreQuantity() {
      const url = new URL(location.href);
      if (!url.searchParams.has('ph_login_qty')) return;
      const quantity = Number(url.searchParams.get('ph_login_qty'));
      if (!Number.isSafeInteger(quantity) || quantity < 1) return;
      await Promise.all([customElements.whenDefined('product-form-component'), customElements.whenDefined('quantity-selector-component')]);
      if (!this.isConnected) return;
      const form = this.productForm();
      const input = form?.querySelector('[name="quantity"]');
      const variant = form?.querySelector('[name="id"]')?.value;
      if (!input || variant !== url.searchParams.get('variant')) return;
      const min = Number(input.min) || 1;
      const selector = input.closest('quantity-selector-component');
      const max = selector?.getEffectiveMax?.() ?? (input.max ? Number(input.max) : Infinity);
      const step = Number(input.step) || 1;
      input.value = String(Math.min(max, min + Math.floor(Math.max(0, quantity - min) / step) * step));
      selector?.updateQuantity(0);
      url.searchParams.delete('ph_login_qty');
      history.replaceState(history.state, '', url.pathname + url.search + url.hash);
      this.prepareLogin();
    }

    updateCount(count) {
      if (!Number.isSafeInteger(count) || count < 0) return;
      this.querySelector('[data-header-cart-count]').textContent = count > 99 ? '99+' : String(count);
      this.querySelector('[data-header-cart]').setAttribute('aria-label', this.dataset.cartLabel.replace('__COUNT__', String(count)));
    }

    async refreshCount() {
      try {
        const response = await fetch(this.dataset.cartUrl + '.js', { headers: { Accept: 'application/json' }, signal: this.controller.signal });
        if (response.ok) this.updateCount((await response.json()).item_count);
      } catch (_) { }
    }
  });
}
